/**
 * Foto de perfil, con su control para cambiarla o quitarla.
 *
 * Compuesto sobre `Avatar` (una primitiva de `components/ui.tsx`): esto sí
 * hace `fetch` — sube el archivo y refresca lo que corresponda — y por eso
 * vive en `components/common/`, no junto a las primitivas.
 *
 * Entre elegir el archivo y subirlo hay un paso: `RecorteFoto`. La subida se
 * queda acá y no se mueve para adentro del recortador a propósito — así, si el
 * POST falla, el encuadre sigue en pantalla y se puede reintentar, en vez de
 * mandar a la persona a buscar el archivo otra vez.
 */

import { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { Avatar, Button, Modal } from '../ui';
import { ProductThumb } from './ProductThumb';
import { Icon } from './NavIcons';
import { RecorteFoto } from './RecorteFoto';
import type { Forma } from '@/lib/imagen';

export function PhotoUploader({
  uploadPath, deletePath, photoSrc, name, invalidate, size = 64, onChanged, forma = 'circulo',
}: {
  uploadPath: string;
  deletePath: string;
  /** `null` si todavía no hay foto: `Avatar` cae a las iniciales. */
  photoSrc: string | null;
  name: string;
  invalidate: string[];
  size?: number;
  /** Además de invalidar la caché, algo que no vive en react-query (ej. la sesión). */
  onChanged?: () => void;
  /** Cuadrada para un producto de inventario, que no es una persona. */
  forma?: Forma;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const botonRef = useRef<HTMLButtonElement>(null);
  const [porRecortar, setPorRecortar] = useState<File | null>(null);

  const upload = useApiMutation<unknown, File>(
    (file) => {
      const form = new FormData();
      form.set('file', file);
      return api.post(uploadPath, form);
    },
    invalidate,
    { onSuccess: () => { setPorRecortar(null); devolverFoco(); onChanged?.(); } },
  );
  const remove = useApiMutation(() => api.del(deletePath), invalidate, { onSuccess: () => onChanged?.() });

  /* A mano, porque `Modal` no va a poder: cuando el recortador se monta, el
     elemento con foco es el `<input type="file">`, que está en `display: none`,
     y `focus()` sobre algo oculto no hace nada — el foco terminaba en `<body>`
     y quien navega con teclado volvía al principio de la página. */
  function devolverFoco() {
    botonRef.current?.focus({ preventScroll: true });
  }

  return (
    <div className="row" style={{ gap: 12, alignItems: 'center' }}>
      {forma === 'cuadrado'
        ? <ProductThumb photoSrc={photoSrc} size={size} />
        : <Avatar src={photoSrc} name={name} size={size} />}
      <div style={{ display: 'grid', gap: 4 }}>
        <div className="row" style={{ gap: 6 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) setPorRecortar(file);
              /* Se limpia acá y no adentro del flujo del modal: es lo que
                 permite volver a elegir el MISMO archivo después de cancelar.
                 Movido para adentro, cancelar y reintentar con la misma foto no
                 dispara nada y parece que la aplicación se colgó. */
              e.target.value = '';
            }}
          />
          <Button ref={botonRef} onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
            {upload.isPending ? 'Subiendo…' : photoSrc ? 'Cambiar foto' : 'Subir foto'}
          </Button>
          {photoSrc && (
            /* Un basurero y no la palabra «Quitar» (dueño, 8-set-2026). Al ser
               el único control sin texto de esta fila, lleva `aria-label` y
               `title`: sin eso, un lector de pantalla anuncia «botón» a secas y
               en escritorio no hay forma de saber qué hace sin apretarlo. */
            <Button
              onClick={() => remove.mutate()}
              disabled={remove.isPending}
              aria-label="Quitar la foto"
              title="Quitar la foto"
            >
              <Icon name="basurero" size={16} />
            </Button>
          )}
        </div>
        {/* Antes el límite era invisible hasta que explotaba, que era la queja
            original: se elegía una foto del teléfono y salía un error en rojo. */}
        <span className="small muted">La foto se encuadra y se achica sola.</span>
        {remove.error && (
          <span className="small" style={{ color: 'var(--danger)' }}>{remove.error.message}</span>
        )}
      </div>

      {porRecortar && (
        <Modal
          title="Encuadrar la foto"
          persistente
          onClose={() => { setPorRecortar(null); devolverFoco(); }}
        >
          <RecorteFoto
            archivo={porRecortar}
            forma={forma}
            subiendo={upload.isPending}
            error={upload.error?.message}
            onListo={(recorte) => upload.mutate(recorte)}
            onCancelar={() => { setPorRecortar(null); devolverFoco(); }}
          />
        </Modal>
      )}
    </div>
  );
}
